import {
	computed,
	autorun,
	action,
	runInAction,
	observable,
	reaction,
} from "mobx";

import { HubConnectionFactory, IConnection, HubState } from "@gp/hub";

import { getCurrentLanguage } from "@utils";
import { SportStatisticsHub } from "./SportStatisticsHub";
import { UserAuthStore } from "@state/shared";

/** This is not expected to be disposed during use. */
export class SportStatisticHubStore {
	@observable public hub?: SportStatisticsHub;
	@observable public isSessionExpired: boolean = false;
	private connection?: IConnection;
	private userAuthStore: UserAuthStore;
	private _disconnectDebounce: ReturnType<typeof setTimeout> | undefined;

	@computed public get connectionState() {
		return this.connection?.state;
	}

	@computed private get connectionFactory() {
		const query = new URLSearchParams();
		query.append("shopId", this.userAuthStore.shopId);
		query.append("agency", this.userAuthStore.agencyKey);
		query.append("version", encodeURIComponent(WEBPACK_OFFER_VERSION));
		query.append("language", getCurrentLanguage());
		query.append("callerEnvironment", WEBPACK_ENVIRONMENT_KEY);

		const connectionFactory = new HubConnectionFactory(
			{ get: () => query.toString() },
			{
				serverUrl: WEBPACK_SPORT_STATISTICS_API_ADDRESS + "/sync",
				enableLogging: false,
			}
		);

		return connectionFactory;
	}

	constructor(userAuthStore: UserAuthStore) {
		this.userAuthStore = userAuthStore;
		if (!WEBPACK_IS_SPORT_STATISTICS_ENABLED) {
			return;
		}

		autorun(() => {
			if (this.hub == null || this.isSessionExpired) {
				return;
			}

			if (this.hub.dispatchers.size === 0) {
				this.destroy();
				document.removeEventListener("click", this.clickHandler);
				return;
			}

			document.addEventListener("click", this.clickHandler);
			this.startSession();
		});

		reaction(
			() => this.connectionFactory,
			(connectionFactory) => {
				const prevHubState =
					this.connection?.state || HubState.NOT_INITIALIZED;
				// Disconnect prev connection
				this.connection?.disconnect();
				this.connection = connectionFactory.createConnection();

				// Don't move this it's removes name from connection
				this.hub?.initialize(this.connection);

				switch (prevHubState) {
					case HubState.CONNECTING:
					case HubState.CONNECTED:
					case HubState.AUTO_RECONNECT:
					case HubState.RECONNECTING:
					case HubState.ERROR:
					case HubState.RECONNECTING_AFTER_ERROR:
						if (
							this.hub?.dispatchers &&
							this.hub.dispatchers.size > 0
						) {
							this.startSession();
						}
					default:
					// Empty
				}
			}
		);

		this.connection = this.connectionFactory.createConnection();
		this.connection.connect();
		this.hub = new SportStatisticsHub(this.connection);
		this.hub.initialize();
	}

	@action.bound
	public restartSession() {
		this.isSessionExpired = false;
	}

	@action.bound
	public startSession() {
		if (this.isSessionExpired) {
			// Requires manual session restart.
			return;
		}

		this.restartSession();

		this.connection?.connect().then(() => {
			clearTimeout(this._disconnectDebounce);
			this._disconnectDebounce = undefined;

			this._disconnectDebounce = setTimeout(() => {
				runInAction(() => {
					if (this.hub?.dispatchers.size === 0) {
						console.error("There are no dispatchers");
						return;
					}
					this.connection?.disconnect();
					this.isSessionExpired = true;
				});
			}, 600_000);
		});
	}

	@action.bound
	private destroy() {
		clearTimeout(this._disconnectDebounce);
		this._disconnectDebounce = setTimeout(() => {
			runInAction(() => {
				if (
					this.hub?.dispatchers?.size &&
					this.hub.dispatchers.size > 0
				) {
					console.error("There are dispatchers");
				}
				this.connection?.disconnect();
				this.isSessionExpired = false;
			});
		}, 600_000);
	}

	@action.bound
	private clickHandler = () => {
		if (this.hub?.dispatchers && this.hub?.dispatchers.size > 0) {
			this.startSession();
		}
	};
}
